import { Injectable } from '@angular/core';
import { Subject, zip } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { ErrorType, PageType } from '@lib/interfaces';
import {
  AswMasterService,
  AswServiceStoreService,
  CommonLibService,
  PageInitService,
  PageLoadingService,
} from '@lib/services';
import { EncryptYamatoBaggageRequest, GetOrderRequest } from 'src/sdk-servicing';
import {
  LinkageParameterForSending,
  LinkageParameterForSendingToAllPassenger,
  YamatoBaggageGuidanceParameter,
} from '@common/interfaces/yamato-baggage-guidance';
import { ServicingBaseClass } from '@common/components';
import { RedirectGuidanceService } from '../redirect-guidance.service';
import { GetOrderResultStoreService } from '../get-order-result-store/get-order-result-store.service';
import { EncryptYamatoBaggageResultStoreService } from './encrypt-yamato-baggage-result-store.service';
import { ErrorCodeConstants } from '@conf/app.constants';

@Injectable()
export class YamatoBaggageGuidanceService extends ServicingBaseClass {
  /** ヤマト手荷物案内パラメータ取得通知イベント */
  notifyParameter$ = new Subject<YamatoBaggageGuidanceParameter>();

  /** ヤマト手荷物連携パラメータ暗号化APIレスポンス異常メッセージコード */
  private static readonly ApiErrorMessage: { [errorCode: string]: string } = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000585]: 'E0712',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000615]: 'E0713',
  };

  constructor(
    private _aswServiceStoreService: AswServiceStoreService,
    private _aswMasterService: AswMasterService,
    private _commonLibService: CommonLibService,
    private _pageInitService: PageInitService,
    private _pageLoadingService: PageLoadingService,
    private _redirectGuidanceService: RedirectGuidanceService,
    private _getOrderResultStoreService: GetOrderResultStoreService,
    private _encryptYamatoBaggageResultStoreService: EncryptYamatoBaggageResultStoreService
  ) {
    super();

    this.watchGetOrderSuccess();
    this.watchEncryptYamatoBaggageFailure();

    this.watchSuccesses();
  }

  override destroy(): void {}

  /**
   * ヤマト手荷物案内パラメータを取得する
   */
  getParameter(): void {
    const pnrRequest: GetOrderRequest = {
      credential: {
        firstName: this._aswServiceStoreService.aswServiceData.firstName!,
        lastName: this._aswServiceStoreService.aswServiceData.lastName!,
      },
      skipCheckTasks: true,
      skipWifiInfo: true,
      skipOperationInfo: true,
      skipCprInfo: true,
      skipServicingPapi: true,
    };

    /** サービス共通情報に航空券番号があれば航空券番号を使用してPNR情報取得APIを呼ぶ、
     * 航空券番号がなければ予約番号を使用してPNR情報取得APIを呼ぶ */
    if (this._aswServiceStoreService.aswServiceData.travelDocumentId) {
      pnrRequest.travelDocumentId = this._aswServiceStoreService.aswServiceData.travelDocumentId;
    } else {
      pnrRequest.orderId = this._aswServiceStoreService.aswServiceData.orderId;
    }

    /** PNR情報取得APIを呼び出す */
    this._getOrderResultStoreService.request(pnrRequest);
  }

  /**
   * 外部サイトを表示する
   * @param url 外部サイトURL
   * @param linkageParameterForSending 送信用連携パラメータ
   */
  openUrl(url: string, linkageParameterForSending: LinkageParameterForSending): void {
    this._getOrderResultStoreService.clear();
    this._encryptYamatoBaggageResultStoreService.clear();

    if (this._commonLibService.aswContextStoreService.aswContextData.isAnaApl) {
      // ANAアプリの場合
      this._redirectGuidanceService.anaAplPost(url, linkageParameterForSending);
    } else {
      this._redirectGuidanceService.post(url, linkageParameterForSending);
    }
  }

  /**
   * PNR情報取得APIレスポンス正常取得
   */
  private watchGetOrderSuccess(): void {
    this.subscribeService(this._getOrderResultStoreService.success$, (getOrderResultState) => {
      if (getOrderResultState.model?.data?.orderEligibilities?.yamatoBaggage?.isEligible) {
        const encryptRequest: EncryptYamatoBaggageRequest = {
          credential: {
            firstName: this._aswServiceStoreService.aswServiceData.firstName!,
            lastName: this._aswServiceStoreService.aswServiceData.lastName!,
          },
        };

        /** サービス共通情報に予約番号があれば予約番号を使用してヤマト手荷物連携パラメータ暗号化APIを呼ぶ、
         * 予約番号がなければ航空券番号を使用してヤマト手荷物連携パラメータ暗号化APIを呼ぶ */
        if (this._aswServiceStoreService.aswServiceData.orderId) {
          encryptRequest.orderId = this._aswServiceStoreService.aswServiceData.orderId;
        } else {
          encryptRequest.travelDocumentId = this._aswServiceStoreService.aswServiceData.travelDocumentId;
        }

        this._encryptYamatoBaggageResultStoreService.request(encryptRequest);
      } else {
        this._commonLibService.errorsHandlerService.setRetryableError(PageType.PAGE, {
          errorMsgId: 'E0434',
        });

        this.notifyParameter$.next({
          data: getOrderResultState.model?.data,
        });

        this._pageInitService.endInit();
      }
    });
  }

  /**
   * ヤマト手荷物連携パラメータ暗号化APIレスポンス異常取得
   */
  private watchEncryptYamatoBaggageFailure(): void {
    this.subscribeService(
      this._encryptYamatoBaggageResultStoreService.failure$.pipe(
        withLatestFrom(this._commonLibService.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state)
      ),
      (state) => {
        const apiError = state?.errors?.[0];
        if (apiError) {
          const matchErrorMsgId = YamatoBaggageGuidanceService.ApiErrorMessage[apiError.code];
          if (matchErrorMsgId) {
            this._commonLibService.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.BUSINESS_LOGIC,
              errorMsgId: matchErrorMsgId,
              apiErrorCode: apiError.code,
            });
          } else {
            this._commonLibService.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.SYSTEM,
              apiErrorCode: apiError.code,
            });
          }
        }

        this._pageLoadingService.endLoading();
      }
    );
  }

  /**
   * 各APIレスポンス正常取得
   */
  private watchSuccesses(): void {
    this.subscribeService(
      zip(this._getOrderResultStoreService.success$, this._encryptYamatoBaggageResultStoreService.success$),
      ([getOrderResultState, encryptYamatoBaggageResultState]) => {
        const handBaggageUrl = this._aswMasterService.getMPropertyByKey('otherApplicationSettings', 'handBaggage.url');
        const handBaggageSiteId = this._aswMasterService.getMPropertyByKey(
          'otherApplicationSettings',
          'handBaggage.siteId'
        );
        const linkageParameterForSendingToAllPassenger: LinkageParameterForSendingToAllPassenger = {};

        encryptYamatoBaggageResultState.model?.encryptedList.forEach((encrypted) => {
          linkageParameterForSendingToAllPassenger[encrypted.travelerId] = {
            site_id: handBaggageSiteId,
            auth_cd: encrypted.convertedString,
            hash_cd: encrypted.hash,
          };
        });

        this.notifyParameter$.next({
          url: handBaggageUrl,
          linkageParameter: linkageParameterForSendingToAllPassenger,
          data: getOrderResultState.model?.data,
        });

        this._pageInitService.endInit();
      }
    );
  }
}
